import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Helpers from "../../Config/Helpers";
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell, Alert, CircularProgress, Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const moneyFields = new Set(["totalPrice", "paid", "remaining", "tax", "discountAmount", "oilPrice", "extraOilPrice", "oilFilterPrice", "fullServicePrice", "tiresPrice", "overpaymentAmount"]);
const techNames = (value) => (value.technicians || [value.technician]).filter(Boolean)
  .map((tech) => tech.label || tech.name || tech.value?.name || String(tech)).join(", ");

function SnapshotDetails({ value }) {
  if (value == null || value === "") return <span>—</span>;
  if (typeof value !== "object") return <span>{typeof value === "boolean" ? value ? "Yes" : "No" : String(value)}</span>;
  if (Array.isArray(value)) return <Box>{value.map((item, index) => <Box key={index} sx={{ mb: 1 }}><SnapshotDetails value={item} /></Box>)}</Box>;
  return <Box component="dl" sx={{ m: 0 }}>{Object.entries(value)
    .filter(([key]) => !["_id", "id", "lineItemId", "linkedPartId"].includes(key))
    .map(([key, item]) => <Box key={key} sx={{ mb: 0.5 }}>
      <Typography component="dt" variant="caption" sx={{ fontWeight: 600, textTransform: "capitalize" }}>{key.replace(/([A-Z])/g, " $1").replace(/_/g, " ")}</Typography>
      <Box component="dd" sx={{ ml: 1 }}><SnapshotDetails value={item} /></Box>
    </Box>)}</Box>;
}

export function HistoryValue({ field, value }) {
  return <>
    {formatHistoryValue(field, value)}
    {value && typeof value === "object" && <details style={{ marginTop: 8 }}>
      <summary>View full details</summary><SnapshotDetails value={value} />
    </details>}
  </>;
}

export function formatHistoryValue(field, value) {
  if (value == null || value === "") return "—";
  if (moneyFields.has(field)) return `$${Number(value).toFixed(2)}`;
  if (field === "overpaymentDisposition") return value === "customer_credit" ? "Customer credit" : "Refund due";
  if (field === "date") return new Date(value).toLocaleDateString();
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (!value.length) return "None";
    if (field === "payments") return value.map((payment) =>
      [`$${Number(payment.amount_paid || 0).toFixed(2)}`, payment.payment_type, payment.payment_date, payment.note].filter(Boolean).join(" · ")).join("\n");
    return value.map((item) => {
      const line = item?.value || item;
      if (typeof line !== "object" || line === null) return String(line);
      const name = line.name || line.service_name || [line.brand, line.size].filter(Boolean).join(" ");
      const quantity = line.quantity == null ? "" : `Qty ${line.quantity}`;
      const price = line.price ?? line.sellPrice;
      const technicians = techNames(line);
      const parts = (line.linkedParts || []).map((part) => `${part.name || "Part"} (Qty ${part.quantity || 1})`).join(", ");
      return [name, quantity, price == null ? "" : `$${Number(price).toFixed(2)} each`, line.description,
        line.tax, technicians ? `Tech: ${technicians}` : "", parts ? `Parts: ${parts}` : ""].filter(Boolean).join(" · ");
    }).join("\n");
  }
  if (typeof value === "object") {
    if (field === "vehicle") return [value.year, value.make || value.name, value.model, value.vin_number].filter(Boolean).join(" · ");
    return [value.name || value.label || value.brand, value.phone, value.email, value.address].filter(Boolean).join(" · ") || "—";
  }
  return String(value);
}

function ReceiptEditHistory({ receiptId, refreshKey }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return axios.get(`${Helpers.baseUrl}/receipts/edit-history/${receiptId}`, Helpers.headers)
      .then(({ data }) => setHistory(Array.isArray(data) ? data : []))
      .catch(() => setError("Could not load edit history. Please try again."))
      .finally(() => setLoading(false));
  }, [receiptId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return (
    <Box sx={{ mt: 3, borderTop: "1px solid #e5e7eb", pt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Edit History</Typography>
      {loading ? <CircularProgress size={22} /> : error ? <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>
        : !history.length ? <Typography color="text.secondary">No recorded edits yet. Older edits made before history was enabled are not available.</Typography>
          : history.map((entry) => (
            <Accordion key={entry._id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{entry.editedByName} · {new Date(entry.createdAt).toLocaleString()}</Typography>
                  <Typography variant="body2">{entry.reason}</Typography>
                  <Typography variant="caption" color="text.secondary">{entry.changes.map((change) => change.label).join(", ")}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label="Receipt changes">
                  <TableHead><TableRow><TableCell>Changed</TableCell><TableCell>Before</TableCell><TableCell>After</TableCell></TableRow></TableHead>
                  <TableBody>{entry.changes.map((change) => (
                    <TableRow key={change.field}>
                      <TableCell>{change.label}</TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap", minWidth: 180 }}><HistoryValue field={change.field} value={change.before} /></TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap", minWidth: 180 }}><HistoryValue field={change.field} value={change.after} /></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}
    </Box>
  );
}

export default ReceiptEditHistory;
